import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useSettings } from "@/hooks/useSettings";
import { ShieldCheck, Lock, Ban, EyeOff, Link2, Twitter } from "lucide-react";
import api from "@/lib/api";
import { withXHandle, xHandleFromUrl } from "@/lib/memeKit";
import { renderTokenomicsCard } from "@/lib/tokenomicsCard";
import ShareToXDialog from "@/components/ShareToXDialog";

const COLORS = ["#00FF66", "#A855F7", "#FFD700", "#FF3366", "#F5F5F0"];

const PRINCIPLES = [
  { icon: Lock, t: "Fixed Supply", d: "No hidden minting. What's planned is what exists." },
  { icon: EyeOff, t: "No Reflection", d: "No weird transfer tax skimming your bag." },
  { icon: Ban, t: "No Blacklist", d: "No secret blacklist, no arbitrary confiscation." },
  { icon: ShieldCheck, t: "No Backdoors", d: "No hidden admin backdoors or misleading mechanics." },
];

const FALLBACK = {
  plannedTotalSupply: 1000000000,
  label: "PLANNED — NOT DEPLOYED",
  allocation: [
    { name: "Public / Community", percent: 90, amount: 900000000, desc: "The majority of the planned supply is allocated to the public and community." },
    { name: "Community Rewards & Airdrops", percent: 5, amount: 50000000, desc: "Reserved for future community participation, rewards and qualifying distribution programs. Specific eligibility rules will be announced separately." },
    { name: "Liquidity", percent: 2, amount: 20000000, desc: "Reserved for initial and ongoing liquidity requirements across the planned supported ecosystems." },
    { name: "Treasury", percent: 2, amount: 20000000, desc: "Reserved for future ecosystem development, operations, partnerships and community initiatives." },
    { name: "Team", percent: 1, amount: 10000000, desc: "Reserved for the team. Vesting/lockup terms will be published before launch." },
  ],
  liquiditySplit: [
    { chain: "Solana", percent: 70, amount: 14000000 },
    { chain: "Robinhood Chain", percent: 30, amount: 6000000 },
  ],
  multiChainNote: "$ROBBED is planned as one unified 1 billion token supply across Solana and Robinhood Chain. The two ecosystems do not represent separate token supplies.",
  liquidityNote: "70% of the planned liquidity allocation is intended for Solana and 30% for Robinhood Chain. Final deployment amounts and liquidity pairs will be determined before launch.",
};

export default function Tokenomics() {
  const { data } = useSettings();
  const tk = data?.tokenomics || FALLBACK;
  const num = (n) => Number(n).toLocaleString();

  // Tokenomics share card — reuses ShareToXDialog + @robbedcoin single source. NO AI, no duplicate values.
  const cardRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [canNative, setCanNative] = useState(false);
  const blobRef = useRef(null);
  const fileRef = useRef(null);
  const textRef = useRef("");
  const openShare = async () => {
    const handle = xHandleFromUrl(data?.xUrl) || "@robbedcoin";
    renderTokenomicsCard(cardRef.current, tk, handle);
    textRef.current = withXHandle(
      `$ROBBED TOKENOMICS — ${num(tk.plannedTotalSupply)} total PLANNED supply. 90% Public/Community, 5% Rewards & Airdrops, 2% Liquidity, 2% Treasury, 1% Team. One unified supply across Solana + Robinhood Chain.`,
      data?.xUrl);
    let p = null; try { p = cardRef.current.toDataURL("image/png"); } catch { /* noop */ }
    setPreview(p);
    let blob = null; try { blob = await new Promise((r) => cardRef.current.toBlob(r, "image/png")); } catch { /* noop */ }
    blobRef.current = blob;
    const file = blob ? new File([blob], "robbed-tokenomics.png", { type: "image/png" }) : null;
    fileRef.current = file;
    setCanNative(!!(file && navigator.canShare && navigator.canShare({ files: [file] })));
    setShareOpen(true);
  };
  const doNativeShare = async () => {
    try {
      await navigator.share({ files: [fileRef.current], text: textRef.current });
      api.post("/analytics/event", { name: "tokenomics_share_x", meta: { method: "native", image: "attached" } }).catch(() => {});
      setShareOpen(false);
    } catch (err) { if (err?.name === "AbortError") return; }
  };
  const onManualInitiated = () => {
    api.post("/analytics/event", { name: "tokenomics_share_x", meta: { image: "manual" } }).catch(() => {});
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="tokenomics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <span className="tag bg-danger text-white" data-testid="tokenomics-planned-label">{tk.label}</span>
        <button onClick={openShare} className="btn-neon !py-2 !px-4 !text-sm" data-testid="tokenomics-share-btn">
          <Twitter className="w-4 h-4" /> Share Tokenomics on X
        </button>
      </div>
      <h1 className="headline text-cream text-4xl sm:text-6xl mt-5">$ROBBED <span className="text-neon">TOKENOMICS</span></h1>

      {/* Total planned supply hero */}
      <div className="comic-card p-6 sm:p-8 mt-6 bg-ink3">
        <p className="font-heading font-bold uppercase text-dim text-sm tracking-wide">Total Planned Supply</p>
        <p className="font-display font-900 text-neon text-4xl sm:text-6xl mt-2" data-testid="tokenomics-total-supply">
          {num(tk.plannedTotalSupply)} <span className="text-cream">$ROBBED</span>
        </p>
        <p className="font-mono text-xs sm:text-sm text-dim mt-3 max-w-2xl">
          These figures are the <span className="text-cream font-bold">planned allocation</span>, not circulating supply.
          Nothing is deployed, minted, locked or vested on-chain yet.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        {/* Allocation */}
        <div className="comic-card p-6 sm:p-8">
          <h3 className="font-display font-900 text-cream text-xl uppercase mb-6">Planned Allocation</h3>
          <div className="w-full h-6 flex border-3 border-black overflow-hidden mb-8">
            {tk.allocation.map((a, i) => (
              <div key={a.name} style={{ width: `${a.percent}%`, background: COLORS[i % COLORS.length] }} title={`${a.name} ${a.percent}%`} />
            ))}
          </div>
          <div className="space-y-4">
            {tk.allocation.map((a, i) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="border-b border-charcoal pb-3"
                data-testid={`alloc-${i}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-4 h-4 border-2 border-black shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="font-mono text-sm text-cream">{a.name}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-display font-900 text-neon block leading-none" data-testid={`alloc-${i}-percent`}>{a.percent}%</span>
                    <span className="font-mono text-[11px] text-cream/80" data-testid={`alloc-${i}-amount`}>{num(a.amount)} $ROBBED</span>
                  </div>
                </div>
                {a.desc && <p className="font-mono text-[11px] text-dim mt-1.5 leading-relaxed pl-7">{a.desc}</p>}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Principles + Multi-chain + Liquidity + Team */}
        <div className="grid sm:grid-cols-2 gap-4 content-start">
          {PRINCIPLES.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="comic-card p-5"
            >
              <p.icon className="w-7 h-7 text-neon" />
              <h4 className="font-display font-900 text-cream mt-3 uppercase text-sm">{p.t}</h4>
              <p className="font-mono text-xs text-dim mt-2 leading-relaxed">{p.d}</p>
            </motion.div>
          ))}

          {/* Multi-chain */}
          <div className="comic-card p-5 sm:col-span-2 bg-ink3" data-testid="tokenomics-multichain">
            <div className="flex items-center gap-2">
              <Link2 className="w-6 h-6 text-neon" />
              <h4 className="font-display font-900 text-neon uppercase text-sm">Multi-Chain · One Unified Supply</h4>
            </div>
            <p className="font-mono text-xs text-cream/90 mt-2 leading-relaxed">{tk.multiChainNote}</p>
          </div>

          {/* Liquidity split */}
          {tk.liquiditySplit && (
            <div className="comic-card p-5 sm:col-span-2" data-testid="tokenomics-liquidity-split">
              <h4 className="font-display font-900 text-cream uppercase text-sm">Planned Liquidity Split <span className="text-dim text-[10px]">(preliminary)</span></h4>
              <div className="grid grid-cols-2 gap-3 mt-3">
                {tk.liquiditySplit.map((l) => (
                  <div key={l.chain} className="border-3 border-black bg-ink p-3" data-testid={`liq-${l.chain.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="font-display font-900 text-neon text-lg leading-none">{num(l.amount)}</p>
                    <p className="font-mono text-[11px] text-cream mt-1">$ROBBED · {l.chain}</p>
                    <p className="font-mono text-[10px] text-dim">{l.percent}% of liquidity</p>
                  </div>
                ))}
              </div>
              {tk.liquidityNote && <p className="font-mono text-[10px] text-dim mt-3 leading-relaxed">{tk.liquidityNote}</p>}
            </div>
          )}

          {/* Team */}
          <div className="comic-card p-5 sm:col-span-2 bg-ink3">
            <h4 className="font-display font-900 text-neon uppercase text-sm">Team Allocation (Planned)</h4>
            <p className="font-mono text-xs text-dim mt-2">
              1% team allocation. Vesting / lockup terms will be published before launch. Nothing is locked or vested
              until implemented and verifiable on-chain.
            </p>
          </div>
        </div>
      </div>

      <canvas ref={cardRef} className="hidden" aria-hidden="true" />
      <ShareToXDialog
        open={shareOpen} onOpenChange={setShareOpen}
        preview={preview} canNative={canNative}
        blob={blobRef.current} filename="robbed-tokenomics.png" text={textRef.current}
        onNativeShare={doNativeShare} onManualInitiated={onManualInitiated}
        testidPrefix="tok-share" title="Share Tokenomics on X"
      />
    </div>
  );
}
